import { Button } from "@/components/ui/button";
import { X, Clock, Sparkles, User, Calendar } from "lucide-react";
import { TYPOGRAPHY_ROLE_CLASSES } from "@/utils/typographyTokens";

interface InvitationBannerProps {
  invitation: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    expiresAt: number;
    token: string;
  };
  onDismiss: () => void;
}

export function InvitationBanner({
  invitation,
  onDismiss,
}: InvitationBannerProps) {
  const tentativeName = invitation.firstName
    ? `${invitation.firstName} ${invitation.lastName || ""}`.trim()
    : "";
  const nameIsUnknown = /^unknown(\s+user)?$/i.test(tentativeName);
  const recipientName =
    !nameIsUnknown && tentativeName ? tentativeName : invitation.email;

  const expirationDate = new Date(invitation.expiresAt);
  const now = new Date();
  const timeLeft = expirationDate.getTime() - now.getTime();
  const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));

  const isExpired = timeLeft <= 0;
  const isExpiringSoon = daysLeft <= 3 && daysLeft > 0;

  const handleClaimSite = () => {
    window.location.href = `/auth/invitation/${invitation.token}`;
  };

  if (isExpired) {
    return (
      <div className="relative bg-gradient-to-r from-red-500/10 via-red-400/5 to-red-500/10 border-b border-red-200/50 backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-red-50/80 to-red-100/40"></div>

        {/* Dismiss button in top right */}
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-1.5 rounded-full hover:bg-red-100/50 transition-colors text-red-500 hover:text-red-700"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative px-4 sm:px-6 py-3 sm:py-4 pr-6 sm:pr-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
              <div className="flex-shrink-0 p-2 bg-red-100 rounded-full">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-red-900 mb-1">
                  Invitation Expired
                </p>
                <p className={`${TYPOGRAPHY_ROLE_CLASSES.bodySm} text-red-700/80 leading-relaxed`}>
                  Please contact the sender to request a new invitation
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden border-b backdrop-blur-sm ${
        isExpiringSoon
          ? "bg-gradient-to-r from-amber-500/10 via-orange-400/5 to-amber-500/10 border-amber-200/50"
          : "bg-gradient-to-r from-blue-500/10 via-indigo-400/5 to-purple-500/10 border-blue-200/50"
      }`}
    >
      {/* Background overlay */}
      <div
        className={`absolute inset-0 ${
          isExpiringSoon
            ? "bg-gradient-to-r from-amber-50/80 to-orange-100/40"
            : "bg-gradient-to-r from-blue-50/80 to-indigo-100/40"
        }`}
      ></div>

      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_20%_50%,_theme(colors.white)_0%,_transparent_50%)] mix-blend-overlay"></div>

      {/* Dismiss button in top right corner */}
      <button
        onClick={onDismiss}
        className={`absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-2 rounded-lg transition-all duration-200 sm:hover:scale-110 ${
          isExpiringSoon
            ? "text-amber-500 hover:text-amber-700 hover:bg-amber-100/50"
            : "text-blue-500 hover:text-blue-700 hover:bg-blue-100/50"
        }`}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="relative px-4 sm:px-6 py-4 sm:py-5 pr-12 sm:pr-16">
        <div className="max-w-7xl mx-auto">
          {/* Content and CTA layout */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start sm:items-center space-x-3 sm:space-x-5 flex-1 min-w-0">
              {/* Enhanced icon container - smaller on mobile */}
              <div
                className={`flex-shrink-0 p-2 sm:p-3 rounded-lg sm:rounded-xl shadow-sm ${
                  isExpiringSoon
                    ? "bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200/50"
                    : "bg-gradient-to-br from-blue-100 to-indigo-100 border border-blue-200/50"
                }`}
              >
                <div className="relative">
                  <Sparkles
                    className={`h-4 w-4 sm:h-5 sm:w-5 ${
                      isExpiringSoon ? "text-amber-600" : "text-blue-600"
                    }`}
                  />
                  {/* Subtle glow effect - hidden on mobile for performance */}
                  <div
                    className={`hidden sm:block absolute inset-0 rounded-full blur-sm opacity-30 ${
                      isExpiringSoon ? "bg-amber-400" : "bg-blue-400"
                    }`}
                  ></div>
                </div>
              </div>

              <div className="space-y-1 flex-1 min-w-0">
                {/* Main message with better typography */}
                <div className="flex items-center space-x-2">
                  <User
                    className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${
                      isExpiringSoon ? "text-amber-600" : "text-blue-600"
                    }`}
                  />
                  <p
                    className={`text-base font-semibold truncate ${
                      isExpiringSoon ? "text-amber-900" : "text-blue-900"
                    }`}
                  >
                    Welcome {recipientName}!
                  </p>
                </div>
                <p
                  className={`${TYPOGRAPHY_ROLE_CLASSES.bodySm} font-medium leading-relaxed ${
                    isExpiringSoon ? "text-amber-800/90" : "text-blue-800/90"
                  }`}
                >
                  Your personalized website preview is ready
                </p>

                {/* Enhanced expiration info */}
                <div className="flex items-center space-x-1.5 mt-2">
                  <Calendar
                    className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${
                      isExpiringSoon ? "text-amber-600" : "text-blue-600"
                    }`}
                  />
                  <p
                    className={`${TYPOGRAPHY_ROLE_CLASSES.bodySm} ${
                      isExpiringSoon ? "text-amber-700/80" : "text-blue-700/80"
                    }`}
                  >
                    {isExpiringSoon
                      ? `Expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`
                      : `Valid until ${expirationDate.toLocaleDateString()}`}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA button - now standalone and more prominent */}
            <div className="flex justify-center sm:justify-end sm:flex-shrink-0">
              <Button
                onClick={handleClaimSite}
                size="sm"
                className={`font-semibold px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg transition-all duration-200 sm:transform sm:hover:scale-105 shadow-sm w-full sm:w-auto ${
                  isExpiringSoon
                    ? "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 shadow-amber-200"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-blue-200"
                } text-white border-0`}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                <span className="text-base font-semibold">Claim Your Site</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
